import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import { setCredentials } from "../../store/slices/authSlice";
import { authService, searchService } from "../../services/api";
import CustomDropdown from "../common/CustomDropdown";
import { useDebounce } from "../../hooks/useDebounce";

const StaffDetailsStep = () => {
  const dispatch = useDispatch();
  const { error, invitationContext } = useSelector((state) => state.signup);
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [isRoleTypeLocked, setIsRoleTypeLocked] = useState(false);

  // Search states for coach search
  const [coachSearchQuery, setCoachSearchQuery] = useState("");
  const [coachSearchResults, setCoachSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Search states for club search (non-coach roles when works_for_club=true)
  const [clubSearchQuery, setClubSearchQuery] = useState("");
  const [clubSearchResults, setClubSearchResults] = useState([]);
  const [clubSearchPagination, setClubSearchPagination] = useState(null);
  const [isClubSearching, setIsClubSearching] = useState(false);
  const [clubCurrentPage, setClubCurrentPage] = useState(1);
  const [showClubSearchModal, setShowClubSearchModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);

  const searchInputRef = useRef(null);
  const clubSearchInputRef = useRef(null);
  const debouncedSearchQuery = useDebounce(coachSearchQuery, 300);
  const debouncedClubSearchQuery = useDebounce(clubSearchQuery, 300);

  const [formData, setFormData] = useState({
    staff_type: "",
    role_type: "",
    works_for_club: null, // Will be derived by backend for coach roles
    wyscout_club_id: null,
    wyscout_coach_id: null,
    additional_qualifications: "",
    additional_roles: "",
    club_license_number: "",
  });

  // Role type options (categories shown in the signup picker)
  const roleTypes = [
    { value: "leadership", label: "Leadership" },
    { value: "coach", label: "Coach" },
    { value: "technical", label: "Technical/Director" },
    { value: "admin", label: "Club Administration" },
    { value: "medical", label: "Medical" },
    { value: "scouting", label: "Scouting/Management" },
    { value: "other", label: "Other" },
  ];

  // Staff type mapping based on role type. New titles append to existing
  // categories (semantically) and there are two new categories: leadership
  // (CEO group) and admin (Club Admin group).
  const getStaffTypesForRole = (roleType) => {
    switch (roleType) {
      case "leadership":
        return [
          { value: "ceo", label: "CEO" },
          { value: "president", label: "President" },
          { value: "club_owner", label: "Club Owner" },
          { value: "chairman", label: "Chairman" },
          { value: "managing_director", label: "Managing Director" },
          { value: "general_manager", label: "General Manager" },
        ];
      case "coach":
        return [
          { value: "head_coach", label: "Head Coach" },
          { value: "manager", label: "Manager" },
          { value: "first_team_coach", label: "First Team Coach" },
          { value: "assistant_coach", label: "Assistant Coach" },
          { value: "goalkeeping_coach", label: "Goalkeeping Coach" },
          { value: "youth_coach", label: "Youth Coach" },
          {
            value: "strength_and_conditioning_coach",
            label: "Strength & Conditioning Coach",
          },
        ];
      case "technical":
        return [
          { value: "technical_director", label: "Technical Director" },
          { value: "head_of_football", label: "Head of Football" },
          { value: "director_of_football_methodology", label: "Director of Football Methodology" },
          { value: "sporting_director", label: "Sporting Director" },
          { value: "director_of_football", label: "Director of Football" },
          { value: "head_of_football_operations", label: "Head of Football Operations" },
          { value: "football_director", label: "Football Director" },
          { value: "analysis", label: "Analysis" },
          { value: "operations", label: "Operations" },
          { value: "academy_director", label: "Academy Director" },
          { value: "youth_coordinator", label: "Youth Coordinator" },
        ];
      case "admin":
        return [
          { value: "club_admin", label: "Club Admin" },
          { value: "club_secretary", label: "Club Secretary" },
          { value: "general_secretary", label: "General Secretary" },
          { value: "head_of_administration", label: "Head of Administration" },
          { value: "football_operations_manager", label: "Football Operations Manager" },
          { value: "transfer_manager", label: "Transfer Manager" },
        ];
      case "medical":
        return [
          { value: "medical", label: "Medical" },
          { value: "physiotherapist", label: "Physiotherapist" },
          { value: "sports_doctor", label: "Sports Doctor" },
          { value: "nutritionist", label: "Nutritionist" },
          { value: "sports_psychologist", label: "Sports Psychologist" },
        ];
      case "scouting":
        return [
          { value: "team_manager", label: "Team Manager" },
          { value: "scout", label: "Scout" },
          { value: "head_of_scouting", label: "Head of Scouting" },
          { value: "head_of_recruitment", label: "Head of Recruitment" },
          { value: "chief_scout", label: "Chief Scout" },
          { value: "head_of_analysis", label: "Head of Analysis" },
          { value: "head_of_recruitment_analysis", label: "Head of Recruitment Analysis" },
          { value: "head_of_player_identification", label: "Head of Player Identification" },
          { value: "head_of_football_analytics", label: "Head of Football Analytics" },
          { value: "head_of_data", label: "Head of Data" },
          { value: "recruitment_director", label: "Recruitment Director" },
          { value: "analyst", label: "Analyst" },
          { value: "performance_analyst", label: "Performance Analyst" },
          { value: "video_analyst", label: "Video Analyst" },
          { value: "data_analyst", label: "Data Analyst" },
          { value: "recruitment_analyst", label: "Recruitment Analyst" },
          { value: "opposition_analyst", label: "Opposition Analyst" },
          { value: "youth_scout", label: "Youth Scout" },
        ];
      case "other":
        return [{ value: "other", label: "Other" }];
      default:
        return [];
    }
  };

  // Map UI selections (category + staff_type option) to canonical role_type and staff_type
  const mapSelectionToCanonical = (category, selectedStaffType) => {
    if (!category || !selectedStaffType) return null;

    // Coach category → canonical role and staff type
    if (category === "coach") {
      if (
        [
          "head_coach",
          "manager",
          "first_team_coach",
          "assistant_coach",
          "goalkeeping_coach",
          "strength_and_conditioning_coach",
        ].includes(selectedStaffType)
      ) {
        return { role_type: selectedStaffType, staff_type: "coach" };
      }
      if (selectedStaffType === "youth_coach") {
        return { role_type: "youth_coach", staff_type: "academy" };
      }
    }

    // Leadership category (CEO group)
    if (category === "leadership") {
      if (
        [
          "ceo",
          "president",
          "club_owner",
          "chairman",
          "managing_director",
          "general_manager",
        ].includes(selectedStaffType)
      ) {
        return { role_type: selectedStaffType, staff_type: "ceo" };
      }
    }

    // Club Administration category
    if (category === "admin") {
      if (
        [
          "club_admin",
          "club_secretary",
          "general_secretary",
          "head_of_administration",
          "football_operations_manager",
          "transfer_manager",
        ].includes(selectedStaffType)
      ) {
        return { role_type: selectedStaffType, staff_type: "club_admin" };
      }
    }

    // Technical category
    if (category === "technical") {
      // Technical Director group
      if (
        [
          "technical_director",
          "head_of_football",
          "director_of_football_methodology",
        ].includes(selectedStaffType)
      ) {
        return {
          role_type: selectedStaffType,
          staff_type: "technical_director",
        };
      }
      // Sporting Director group
      if (
        [
          "sporting_director",
          "director_of_football",
          "head_of_football_operations",
          "football_director",
        ].includes(selectedStaffType)
      ) {
        return {
          role_type: selectedStaffType,
          staff_type: "sporting_director",
        };
      }
      if (selectedStaffType === "analysis") {
        return { role_type: "performance_analyst", staff_type: "analysis" };
      }
      if (selectedStaffType === "academy_director") {
        return { role_type: "academy_director", staff_type: "academy" };
      }
      if (selectedStaffType === "youth_coordinator") {
        return { role_type: "youth_coordinator", staff_type: "academy" };
      }
    }

    // Scouting/Management category
    if (category === "scouting") {
      if (selectedStaffType === "team_manager") {
        return { role_type: "team_manager", staff_type: "team_manager" };
      }
      // Scout Team values
      if (
        [
          "scout",
          "analyst",
          "performance_analyst",
          "video_analyst",
          "data_analyst",
          "recruitment_analyst",
          "opposition_analyst",
        ].includes(selectedStaffType)
      ) {
        return { role_type: selectedStaffType, staff_type: "scout" };
      }
      // Head of Scouting group
      if (
        [
          "head_of_scouting",
          "head_of_recruitment",
          "chief_scout",
          "head_of_analysis",
          "head_of_recruitment_analysis",
          "head_of_player_identification",
          "head_of_football_analytics",
          "head_of_data",
          "recruitment_director",
        ].includes(selectedStaffType)
      ) {
        return {
          role_type: selectedStaffType,
          staff_type: "head_of_scouting",
        };
      }
      if (selectedStaffType === "youth_scout") {
        return { role_type: "youth_scout", staff_type: "academy" };
      }
    }

    // Medical category
    if (category === "medical") {
      if (
        [
          "physiotherapist",
          "sports_doctor",
          "nutritionist",
          "sports_psychologist",
        ].includes(selectedStaffType)
      ) {
        return { role_type: selectedStaffType, staff_type: "medical" };
      }
      if (selectedStaffType === "medical") {
        return { role_type: "medical_staff", staff_type: "medical" };
      }
    }

    // Other category
    if (category === "other") {
      return { role_type: "other", staff_type: "other" };
    }

    return null;
  };

  // Map canonical role_type from invitation to UI category and staff_type default
  const mapCanonicalRoleToSelections = (canonicalRole) => {
    // CEO group → leadership category
    if ([
      "ceo", "president", "club_owner", "chairman",
      "managing_director", "general_manager",
    ].includes(canonicalRole)) {
      return { category: "leadership", staff_type: canonicalRole };
    }
    // Club Admin group → admin category
    if ([
      "club_admin", "club_secretary", "general_secretary",
      "head_of_administration", "football_operations_manager", "transfer_manager",
    ].includes(canonicalRole)) {
      return { category: "admin", staff_type: canonicalRole };
    }
    // Head Coach group + Co Trainer group → coach category
    if ([
      "head_coach", "manager", "first_team_coach",
      "assistant_coach", "goalkeeping_coach",
      "strength_and_conditioning_coach", "youth_coach",
    ].includes(canonicalRole)) {
      return { category: "coach", staff_type: canonicalRole };
    }
    // Technical Director + Sporting Director groups → technical category
    if ([
      "technical_director", "head_of_football", "director_of_football_methodology",
      "sporting_director", "director_of_football", "head_of_football_operations",
      "football_director",
    ].includes(canonicalRole)) {
      return { category: "technical", staff_type: canonicalRole };
    }
    // Head of Scouting + Scout Team groups → scouting category
    if ([
      "head_of_scouting", "head_of_recruitment", "chief_scout",
      "head_of_analysis", "head_of_recruitment_analysis",
      "head_of_player_identification", "head_of_football_analytics",
      "head_of_data", "recruitment_director",
      "scout", "youth_scout", "analyst", "performance_analyst",
      "video_analyst", "data_analyst", "recruitment_analyst",
      "opposition_analyst",
    ].includes(canonicalRole)) {
      return { category: "scouting", staff_type: canonicalRole };
    }
    // Legacy mappings
    switch (canonicalRole) {
      case "academy_director":
        return { category: "technical", staff_type: "academy_director" };
      case "youth_coordinator":
        return { category: "technical", staff_type: "youth_coordinator" };
      case "team_manager":
        return { category: "scouting", staff_type: "team_manager" };
      case "physiotherapist":
        return { category: "medical", staff_type: "physiotherapist" };
      case "sports_doctor":
        return { category: "medical", staff_type: "sports_doctor" };
      case "nutritionist":
        return { category: "medical", staff_type: "nutritionist" };
      case "sports_psychologist":
        return { category: "medical", staff_type: "sports_psychologist" };
      case "medical_staff":
        return { category: "medical", staff_type: "medical" };
      case "other":
      default:
        return { category: "other", staff_type: "other" };
    }
  };

  // Auto-select and lock role type for invited staff
  useEffect(() => {
    if (
      invitationContext?.isInvitationFlow &&
      invitationContext?.type === "staff" &&
      invitationContext?.roleType
    ) {
      const mapped = mapCanonicalRoleToSelections(invitationContext.roleType);
      setFormData((prev) => ({
        ...prev,
        role_type: mapped.category,
        staff_type: mapped.staff_type,
      }));
      setIsRoleTypeLocked(true);
    }
  }, [invitationContext]);

  // Search coaches when query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchCoaches(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      setCoachSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery, showSearchModal]);

  // Search clubs when query changes
  useEffect(() => {
    if (debouncedClubSearchQuery.trim() && showClubSearchModal) {
      searchClubs(debouncedClubSearchQuery, 1);
      setClubCurrentPage(1);
    } else if (!debouncedClubSearchQuery.trim()) {
      setClubSearchResults([]);
      setClubSearchPagination(null);
      setIsClubSearching(false);
    }
  }, [debouncedClubSearchQuery, showClubSearchModal]);

  const searchCoaches = async (query, page = 1) => {
    setIsSearching(true);
    try {
      const params = {
        type: "coaches",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params);

      if (response.success && response.data) {
        // Updated to handle new response structure
        const coaches = response.data.results?.coaches || [];
        setCoachSearchResults(coaches);
        setSearchPagination(response.pagination || null);
      } else {
        setCoachSearchResults([]);
        setSearchPagination(null);
      }
    } catch (error) {
      console.error("Coach search error:", error);
      setCoachSearchResults([]);
      setSearchPagination(null);
    } finally {
      setIsSearching(false);
    }
  };

  const searchClubs = async (query, page = 1) => {
    setIsClubSearching(true);
    try {
      const params = {
        type: "clubs",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params);

      if (response.success && response.data) {
        // Updated to handle new response structure
        const clubs = response.data.results?.clubs || [];
        setClubSearchResults(clubs);
        setClubSearchPagination(response.pagination || null);
      } else {
        setClubSearchResults([]);
        setClubSearchPagination(null);
      }
    } catch (error) {
      console.error("Club search error:", error);
      setClubSearchResults([]);
      setClubSearchPagination(null);
    } finally {
      setIsClubSearching(false);
    }
  };

  const handlePageChange = (page) => {
    if (coachSearchQuery.trim()) {
      setCurrentPage(page);
      searchCoaches(coachSearchQuery, page);
    }
  };

  const handleClubPageChange = (page) => {
    if (clubSearchQuery.trim()) {
      setClubCurrentPage(page);
      searchClubs(clubSearchQuery, page);
    }
  };

  const handleCoachSelect = (coach) => {
    setSelectedCoach(coach);
    setShowSearchModal(false);
    setCoachSearchQuery("");
    setCoachSearchResults([]);
    setSearchPagination(null);
    setShowConfirmationModal(true);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleOpenClubSearch = () => {
    setShowClubSearchModal(true);
    setTimeout(() => {
      if (clubSearchInputRef.current) {
        clubSearchInputRef.current.focus();
      }
    }, 100);
  };

  const handleConfirmCoachSelection = () => {
    if (!selectedCoach) return;

    // Set the wyscout_coach_id (map from coach's wyscout_id)
    setFormData((prev) => ({
      ...prev,
      wyscout_coach_id: parseInt(selectedCoach.wyscout_id, 10),
    }));

    // Don't set works_for_club or club_id - backend will derive these automatically
    // based on the coach's teams from Wyscout

    setShowConfirmationModal(false);
  };

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    if (isRoleTypeLocked && field === "role_type") {
      return;
    }
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Reset staff_type when role_type changes
      if (field === "role_type") {
        newData.staff_type = "";
        newData.wyscout_coach_id = null;
        newData.wyscout_club_id = null;
        newData.club_license_number = "";
        // reset any chosen club
        setSelectedClub(null);
        // For coach roles, don't set works_for_club - backend will derive it
        // For non-coach roles, set default works_for_club
        if (value !== "coach") {
          newData.works_for_club = true;
        } else {
          newData.works_for_club = null; // Let backend derive
        }
      }

      // When works_for_club toggles, clear club selection if false
      if (field === "works_for_club") {
        if (value !== true) {
          newData.wyscout_club_id = null;
          newData.club_license_number = "";
          setSelectedClub(null);
        }
      }

      if (field === "wyscout_club_id" && (value === null || value === undefined)) {
        newData.club_license_number = "";
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.staff_type || !formData.role_type) {
      dispatch(setError("Please fill in all required fields"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    // Compute canonical mapping per backend contract
    const canonical = mapSelectionToCanonical(
      formData.role_type,
      formData.staff_type
    );

    if (!canonical) {
      setLoading(false);
      dispatch(
        setError(
          "Invalid role selection. Please review your role and staff type."
        )
      );
      return;
    }

    const staffData = {
      staff_type: canonical.staff_type,
      role_type: canonical.role_type,
      additional_qualifications: String(
        formData.additional_qualifications || ""
      ).trim(),
      additional_roles: String(formData.additional_roles || "").trim(),
    };

    // Add wyscout_coach_id if selected (for coach roles)
    if (formData.wyscout_coach_id) {
      staffData.wyscout_coach_id = formData.wyscout_coach_id;
    }

    // For coach roles with wyscout_coach_id, don't send works_for_club or club association
    // Backend will derive these automatically from the coach's teams

    // For non-coach roles, send club association using Wyscout club id as club_id
    if (formData.role_type !== "coach") {
      if (formData.works_for_club) {
        if (formData.wyscout_club_id) {
          // Send Wyscout club id under wyscout_club_id per requirement
          staffData.wyscout_club_id = formData.wyscout_club_id;

          const clubExistsOnPlatform =
            selectedClub?.platform_data?.exists === true;
          const requiresLicenseNumber =
            Boolean(selectedClub) && !clubExistsOnPlatform;

          if (requiresLicenseNumber) {
            const licenseNumber = String(
              formData.club_license_number || ""
            ).trim();

            if (!licenseNumber) {
              setLoading(false);
              dispatch(
                setError("Please provide your club license number.")
              );
              return;
            }

            staffData.club_license_number = licenseNumber;
          }
        } else {
          setLoading(false);
          dispatch(setError("Please select your club from the search."));
          return;
        }
      }
    }

    console.log("Submitting staff data:", staffData);
    // return
    try {
      const response = await authService.createStaff(staffData);

      if (response.success) {
        console.log("Staff creation successful, updating auth state");

        // Update auth state with staff role after successful creation
        const updatedUser = {
          ...auth.user,
          roles: ["staff"], // Add staff role
          userTypeSelected: true, // Mark onboarding as complete
        };

        const updatedAuthData = {
          user: updatedUser,
          tokens: auth.tokens,
        };

        // Update auth state in Redux and localStorage
        dispatch(setCredentials(updatedAuthData));

        dispatch(setSignupData({ staffDetails: staffData }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(setError(response.error || "Failed to create staff"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  const currentStaffTypes = getStaffTypesForRole(formData.role_type);
  const isCoachRole = formData.role_type === "coach";
  const clubExistsOnPlatform = selectedClub?.platform_data?.exists === true;
  const requiresLicenseNumber =
    Boolean(selectedClub) && selectedClub?.platform_data?.exists !== true;

  return (
    <>
      <div className="py-6">
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5 mr-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            Back
          </button>
        </div>

        <div className="w-full">
          <div className="flex items-center justify-center w-10 h-10 bg-[#374DF5] rounded-lg mb-4">
            <svg
              width="14"
              height="13"
              viewBox="0 0 14 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.26144 11.5897V10.4588C9.26144 9.85886 9.02313 9.28353 8.59894 8.85934C8.17475 8.43515 7.59943 8.19685 6.99953 8.19685H3.0412C2.44131 8.19685 1.86598 8.43515 1.44179 8.85934C1.0176 9.28353 0.779297 9.85886 0.779297 10.4588V11.5897M11.5233 4.23851V7.63137M13.2198 5.93494H9.82692M7.28227 3.67304C7.28227 4.92225 6.26958 5.93494 5.02037 5.93494C3.77115 5.93494 2.75846 4.92225 2.75846 3.67304C2.75846 2.42382 3.77115 1.41113 5.02037 1.41113C6.26958 1.41113 7.28227 2.42382 7.28227 3.67304Z"
                stroke="white"
                stroke-width="1.32577"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
            Setup Staff
          </h2>
          <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
            Please provide your staff information
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Type Selection */}
            <div>
              <CustomDropdown
                label="Role Type *"
                labelClassName="block font-medium text-[#000000] text-base mb-1"
                options={roleTypes}
                value={formData.role_type}
                onChange={(value) => handleInputChange("role_type", value)}
                placeholder="Select your role type"
                displayProperty="label"
                valueProperty="value"
                className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF] ${
                  isRoleTypeLocked
                    ? "bg-gray-100 pointer-events-none opacity-70"
                    : ""
                }`}
              />
              {isRoleTypeLocked && (
                <p className="mt-1 text-xs text-gray-500">
                  Assigned via invitation
                </p>
              )}
            </div>

            {/* Staff Type Selection */}
            {formData.role_type && (
              <div>
                <CustomDropdown
                  label="Staff Type *"
                  labelClassName="block font-medium text-[#000000] text-base mb-1"
                  options={currentStaffTypes}
                  value={formData.staff_type}
                  onChange={(value) => handleInputChange("staff_type", value)}
                  placeholder="Select your staff type"
                  displayProperty="label"
                  valueProperty="value"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                />
              </div>
            )}

            {/* Coach Search (only for coach role) */}
            {isCoachRole && formData.staff_type && (
              <div>
                <label className="block font-medium text-[#000000] text-base mb-2">
                  Search for your coach profile (optional)
                </label>

                <div className="border border-gray-300 rounded-lg h-12 flex items-center">
                  {selectedCoach ? (
                    <div className="flex items-center justify-between w-full px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center"
                          style={{
                            backgroundImage: selectedCoach.image_path
                              ? `url(${selectedCoach.image_path})`
                              : "none",
                          }}
                        />
                        <span className="text-base text-gray-900 font-medium">
                          {selectedCoach.display_name || selectedCoach.name}
                        </span>
                        {selectedCoach.teams &&
                          selectedCoach.teams.length > 0 && (
                            <span className="text-sm text-gray-500">
                              ({selectedCoach.teams[0].name})
                            </span>
                          )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCoach(null);
                          handleInputChange("wyscout_coach_id", null);
                          handleInputChange("club_id", null);
                          handleInputChange("works_for_club", true);
                          handleOpenSearch();
                        }}
                        className="text-secondary hover:text-gray-900"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={handleOpenSearch}
                      className="w-full h-full px-4 text-left"
                    >
                      <span className="text-base text-gray-500">
                        Search for your coach profile...
                      </span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedCoach
                    ? "Coach profile linked. Club association will be automatically derived from your coach's current team."
                    : "Link your Wyscout coach profile to automatically set club association based on your current team."}
                </p>
              </div>
            )}

            {/* Works for Club (only show for non-coach roles) */}
            {!isCoachRole && (
              <div>
                <label className="block font-medium text-[#000000] text-base mb-1">
                  Works for Club
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="works_for_club"
                      checked={formData.works_for_club === true}
                      onChange={() => handleInputChange("works_for_club", true)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="works_for_club"
                      checked={formData.works_for_club === false}
                      onChange={() =>
                        handleInputChange("works_for_club", false)
                      }
                      className="mr-2"
                    />
                    No
                  </label>
                </div>

                {/* Club selection (when works_for_club is true) */}
                {formData.works_for_club === true && (
                  <div className="mt-4">
                    <label className="block font-medium text-[#000000] text-base mb-2">
                      Select Your Club
                    </label>
                    <div className="border border-gray-300 rounded-lg h-12 flex items-center">
                      {selectedClub ? (
                        <div className="flex items-center justify-between w-full px-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center"
                              style={{
                                backgroundImage: selectedClub.image_path
                                  ? `url(${selectedClub.image_path})`
                                  : "none",
                              }}
                            />
                            <span className="text-base text-gray-900 font-medium">
                              {selectedClub.name}
                            </span>
                            {selectedClub.league?.name && (
                              <span className="text-sm text-gray-500">
                                ({selectedClub.league.name})
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedClub(null);
                              setFormData((prev) => ({
                                ...prev,
                                wyscout_club_id: null,
                                club_license_number: "",
                              }));
                              handleOpenClubSearch();
                            }}
                            className="text-secondary hover:text-gray-900"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleOpenClubSearch}
                          className="w-full h-full px-4 text-left"
                        >
                          <span className="text-base text-gray-500">
                            Search for your club...
                          </span>
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {clubExistsOnPlatform
                        ? `This club is already on TransferPitch. We will send your join request to ${
                            selectedClub?.platform_data?.admin_email ||
                            "the club administrator"
                          }.`
                        : "This club is not yet on TransferPitch. Provide the club license number so we can create it for you."}
                    </p>

                    {requiresLicenseNumber && (
                      <div className="mt-4">
                        <label className="block font-medium text-[#000000] text-base mb-1">
                          Club License Number *
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                          placeholder="Enter your club license number"
                          value={formData.club_license_number}
                          onChange={(e) =>
                            handleInputChange(
                              "club_license_number",
                              e.target.value
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block font-medium text-[#000000] text-base mb-1">
                Additional Qualifications
              </label>
              <textarea
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF] resize-none"
                placeholder="Enter additional qualifications (optional)"
                rows="2"
                value={formData.additional_qualifications}
                onChange={(e) =>
                  handleInputChange("additional_qualifications", e.target.value)
                }
              />
            </div>

            <div>
              <label className="block font-medium text-[#000000] text-base mb-1">
                Additional Roles
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                placeholder="e.g., Assistant, Coordinator (optional)"
                value={formData.additional_roles}
                onChange={(e) =>
                  handleInputChange("additional_roles", e.target.value)
                }
              />
            </div>

            <button
              type="submit"
              disabled={loading || !formData.staff_type || !formData.role_type}
              className={`w-full py-2 px-4 rounded-lg transition-colors mb-4 ${
                loading || !formData.staff_type || !formData.role_type
                  ? "bg-[#2283FF1A] text-[#2283FF] cursor-not-allowed"
                  : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
              }`}
            >
              {loading ? "Creating Staff Profile..." : "Create Staff Profile"}
            </button>
          </form>
        </div>
      </div>

      {/* Coach Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-[#202630]">
                Search Coach Profiles
              </h2>
              <button
                type="button"
                onClick={() => setShowSearchModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="p-6 border-b">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Enter coach name to search..."
                  value={coachSearchQuery}
                  onChange={(e) => setCoachSearchQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {isSearching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {isSearching && coachSearchResults.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-secondary">Searching coaches...</p>
                  </div>
                </div>
              ) : coachSearchResults.length > 0 ? (
                <div className="space-y-4">
                  {/* Results List */}
                  <div className="space-y-2">
                    {coachSearchResults.map((coach) => (
                      <button
                        key={coach.id}
                        type="button"
                        onClick={() => handleCoachSelect(coach)}
                        className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                            style={{
                              backgroundImage:
                                coach.image_path &&
                                !coach.image_path.includes("placeholder")
                                  ? `url(${coach.image_path})`
                                  : "none",
                            }}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-base font-medium text-[#1A212F] truncate">
                                {coach.display_name || coach.name}
                              </h3>
                              {coach.date_of_birth && (
                                <span className="text-sm text-secondary flex-shrink-0">
                                  {Math.floor(
                                    (new Date() -
                                      new Date(coach.date_of_birth)) /
                                      (365.25 * 24 * 60 * 60 * 1000)
                                  )}{" "}
                                  years
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="truncate">
                                {coach.firstname} {coach.lastname}
                              </span>

                              {coach.country && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {coach.country.image_path && (
                                    <img
                                      src={coach.country.image_path}
                                      alt={coach.country.name}
                                      className="w-4 h-3 object-cover rounded-sm"
                                    />
                                  )}
                                  <span>{coach.country.name}</span>
                                </div>
                              )}
                            </div>

                            {coach.teams && coach.teams.length > 0 && (
                              <div className="mt-2 text-sm text-gray-500">
                                <span className="font-medium">
                                  Current Team:
                                </span>{" "}
                                {coach.teams[0].name}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {searchPagination && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-secondary">
                          Showing {coachSearchResults.length} results
                        </div>

                        {(searchPagination.total_pages > 1 ||
                          searchPagination.has_more) && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage <= 1}
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Previous
                            </button>

                            <span className="px-2 text-sm text-secondary">
                              {currentPage}
                            </span>

                            <button
                              type="button"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={
                                !searchPagination.has_more &&
                                currentPage >= searchPagination.total_pages
                              }
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : coachSearchQuery.trim() && !isSearching ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-secondary">
                      No coaches found for "{coachSearchQuery}"
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Try adjusting your search terms
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-secondary">
                      Start typing to search for your coach profile
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Enter your name to find your profile in our database
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Club Search Modal */}
      {showClubSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-[#202630]">
                Search Clubs
              </h2>
              <button
                type="button"
                onClick={() => setShowClubSearchModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search Input */}
            <div className="p-6 border-b">
              <div className="relative">
                <input
                  ref={clubSearchInputRef}
                  type="text"
                  placeholder="Enter club name to search..."
                  value={clubSearchQuery}
                  onChange={(e) => setClubSearchQuery(e.target.value)}
                  className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <svg
                  className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {isClubSearching && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-y-auto p-6">
              {isClubSearching && clubSearchResults.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-secondary">Searching clubs...</p>
                  </div>
                </div>
              ) : clubSearchResults.length > 0 ? (
                <div className="space-y-4">
                  {/* Results List */}
                  <div className="space-y-2">
                    {clubSearchResults.map((club) => (
                      <button
                        key={club.id || club.wyscout_id}
                        type="button"
                        onClick={() => {
                          const parseNumericId = (value) => {
                            if (value === null || value === undefined) {
                              return null;
                            }
                            const numeric = Number(value);
                            return Number.isNaN(numeric) ? null : numeric;
                          };

                          const resolvedClubId =
                            parseNumericId(club?.wyscout_id) ??
                            parseNumericId(club?.id);

                          setSelectedClub(club);
                          setShowClubSearchModal(false);
                          setClubSearchQuery("");
                          setClubSearchResults([]);
                          setClubSearchPagination(null);
                          setFormData((prev) => ({
                            ...prev,
                            wyscout_club_id: resolvedClubId,
                            club_license_number: "",
                          }));
                        }}
                        className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                            style={{
                              backgroundImage:
                                club.image_path &&
                                !club.image_path.includes("placeholder")
                                  ? `url(${club.image_path})`
                                  : "none",
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-base font-medium text-[#1A212F] truncate">
                                {club.name}
                              </h3>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              {club.division?.name && (
                                <span className="truncate">
                                  {club.division.name}
                                </span>
                              )}
                              {club.country?.name && (
                                <span className="truncate">{club.country.name}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Pagination */}
                  {clubSearchPagination && (
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-secondary">
                          Showing {clubSearchResults.length} results
                        </div>
                        {(clubSearchPagination.total_pages > 1 ||
                          clubSearchPagination.has_more) && (
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleClubPageChange(clubCurrentPage - 1)
                              }
                              disabled={clubCurrentPage <= 1}
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Previous
                            </button>
                            <span className="px-2 text-sm text-secondary">
                              {clubCurrentPage}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleClubPageChange(clubCurrentPage + 1)
                              }
                              disabled={
                                !clubSearchPagination.has_more &&
                                clubCurrentPage >=
                                  clubSearchPagination.total_pages
                              }
                              className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <svg
                      className="w-12 h-12 text-gray-300 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <p className="text-secondary">
                      Start typing to search for your club
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Enter your club name to find it in our database
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && selectedCoach && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Coach Selection
              </h3>

              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-6">
                <div
                  className="w-16 h-16 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                  style={{
                    backgroundImage:
                      selectedCoach.image_path &&
                      !selectedCoach.image_path.includes("placeholder")
                        ? `url(${selectedCoach.image_path})`
                        : "none",
                  }}
                />
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {selectedCoach.display_name || selectedCoach.name}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedCoach.firstname} {selectedCoach.lastname}
                  </p>
                  {selectedCoach.country && (
                    <div className="flex items-center gap-1 mt-1">
                      {selectedCoach.country.image_path && (
                        <img
                          src={selectedCoach.country.image_path}
                          alt={selectedCoach.country.name}
                          className="w-4 h-3 object-cover rounded-sm"
                        />
                      )}
                      <span className="text-sm text-gray-500">
                        {selectedCoach.country.name}
                      </span>
                    </div>
                  )}
                  {selectedCoach.teams && selectedCoach.teams.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Current Team: {selectedCoach.teams[0].name}
                    </p>
                  )}
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                {selectedCoach.teams && selectedCoach.teams.length > 0
                  ? `This coach is currently associated with ${selectedCoach.teams[0].name}. Club association will be automatically derived by the backend.`
                  : "This coach is not currently associated with any team. Club association will be handled automatically by the backend."}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    setSelectedCoach(null);
                  }}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmCoachSelection}
                  className="flex-1 py-2 px-4 bg-[#2283FF] hover:bg-[#1a6cd4] text-white rounded-lg transition-colors"
                >
                  Confirm Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffDetailsStep;
